/* @jsx h */
import { defineComponent, h, provide, inject } from "vue";
import { createTRPCProxyClient, httpBatchLink, type TRPCLink } from "@trpc/client";
import { observable } from "@trpc/server/observable";
import type { AppRouter } from "@app/server/trpc/router";
import { routingFetch } from "@app/data/helpers";
import * as Sentry from "@sentry/vue";

export type TrpcClient = ReturnType<typeof createTRPCProxyClient<AppRouter>>;

export interface TrpcContextType {
  trpc: TrpcClient;
}

const TrpcContextKey = Symbol("TrpcContext");

// A cancelled request is a deliberate act (a poll timeout, a retry superseding
// an in-flight call, a component unmounting), not a fault worth an issue.
const isAbortError = (error: unknown): boolean =>
  (error instanceof DOMException && error.name === "AbortError") ||
  (error as { name?: string })?.name === "AbortError" ||
  (error as { cause?: { name?: string } })?.cause?.name === "AbortError";

const sentryLink: TRPCLink<AppRouter> = () => {
  return ({ next, op }) => {
    return observable((observer) => {
      return next(op).subscribe({
        next: (value) => observer.next(value),
        error: (err) => {
          if (!isAbortError(err)) {
            Sentry.captureException(err, {
              extra: { path: op.path, type: op.type },
            });
          }
          observer.error(err);
        },
        complete: () => observer.complete(),
      });
    });
  };
};

export const TrpcProvider = defineComponent({
  name: "TrpcProvider",
  setup(_, { slots }) {
    const trpcClient = createTRPCProxyClient<AppRouter>({
      links: [
        sentryLink,
        httpBatchLink({
          url: "/api/trpc",
          // Custom fetch retries across configured servers when a request fails due
          // to server errors (5xx), preferring the primary route first.
          fetch: routingFetch,
        }),
      ],
    });

    provide(TrpcContextKey, { trpc: trpcClient });

    return () => slots.default?.();
  },
});

export function useTrpc(): TrpcClient {
  const context = inject<TrpcContextType>(TrpcContextKey);
  if (!context) {
    throw new Error("useTrpc must be used within a TrpcProvider");
  }
  return context.trpc;
}
